import { REACT_APP_URL_V2_LOGOUT } from '../components/common/Routes/URLs';
import { endSession, isReauthPending, REAUTH_REASON, requestReauth } from '../utils/helpers/sessionManager';
import { getAccessToken } from '../utils/helpers/sessionStore';
import { BACKEND_IDS } from './ApiConfig';
import { getApiService } from './ApiServices';

/**
 * Single entry point for every logout. Storage cleanup, the cross-tab
 * announcement and the redirect all live in sessionManager.endSession, so the
 * three divergent key lists this service used to carry are gone.
 * @author armandoricosandoval -ai-
 */
class LogoutService {
  constructor() {
    this.isLoggingOut = false;
    this.isLocking = false;
  }

  /**
   * Revokes the token server-side, then ends the session in every tab.
   * The revoke call is best-effort: it must never block the local logout.
   */
  async performLogout(reason = 'manual_logout') {
    if (this.isLoggingOut) return;

    this.isLoggingOut = true;

    try {
      const token = getAccessToken();
      if (token) await this.revokeToken(token);
    } finally {
      endSession(reason);
      this.isLoggingOut = false;
    }
  }

  /** Best-effort server-side revoke. Failures are swallowed on purpose. */
  async revokeToken(token) {
    try {
      await getApiService(BACKEND_IDS.BACKEND_2).post(REACT_APP_URL_V2_LOGOUT, { token }, { skipSessionRecovery: true });
    } catch (error) {
      /* the session is ending regardless; the token expires on its own */
    }
  }

  async logoutByTimeout() {
    await this.performLogout('timeout_logout');
  }

  // Idle timeout while a reauthentication screen is mounted: the token is revoked but the screen is kept, locked.
  async lockForReauth() {
    if (this.isLocking || isReauthPending()) return;

    this.isLocking = true;

    try {
      const token = getAccessToken();
      if (token) await this.revokeToken(token);
    } finally {
      requestReauth(REAUTH_REASON.idleLock);
      this.isLocking = false;
    }
  }

  async logoutByUser() {
    await this.performLogout('user_initiated');
  }

  async logoutByTokenError() {
    await this.performLogout('token_error');
  }

  async logoutByDebug() {
    await this.performLogout('debug_initiated');
  }

  isLogoutInProgress() {
    return this.isLoggingOut;
  }
}

const logoutService = new LogoutService();
export default logoutService;
